import { useRef, useEffect } from 'react';
import { debounce } from 'lodash-es';

import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/tabbed-content-underline.module.scss';
import type { ITabs } from './types';

export const TabbedContentUnderline = ({ 
  className = '',
  activeTab, 
  setActiveTab, 
  tabs,
}: ITabs) => {
  const tabRefs = useRef<(HTMLLIElement | null)[]>([]);
  const tabIndicatorRef = useRef<HTMLLIElement>(null);
  const tabContainerRef = useRef<HTMLDivElement>(null);

  const handleClick = (index: number) => {
    setActiveTab(index);
  };

  useEffect(() => {
    const handleTabChange = debounce(() => {
      const activeTabRef = tabRefs.current[activeTab];
      const tabIndicator = tabIndicatorRef.current;

      if (activeTabRef && tabIndicator) {
        const tabRect = activeTabRef.getBoundingClientRect();
        const containerRect = activeTabRef.parentElement?.getBoundingClientRect();

        if (containerRect) {
          tabIndicator.style.width = `${tabRect.width + 5}px`;
          tabIndicator.style.left = `${tabRect.left - containerRect.left}px`;
        }
      }
    });

    handleTabChange();
    
    window.addEventListener('resize', handleTabChange);
    
    return () => window.removeEventListener('resize', handleTabChange);
  }, [activeTab]);

  return (
    <div ref={tabContainerRef} className={generateClassName([styles.tabContainer, className])}>
      <ul className={generateClassName([styles.underlineTabs, 'tabList'])}>
        {tabs.map((tabLabel, index) => (
          <li
            ref={(el) => (tabRefs.current[index] = el)}
            key={tabLabel}
            className={generateClassName([
              styles.underlineTab,
              activeTab === index ? generateClassName([styles.activeTab, 'active']) : '',
              'tabListItem',
            ])}
          >
            <button onClick={() => handleClick(index)}>{tabLabel}</button>
          </li>
        ))}
        <li ref={tabIndicatorRef} className={styles.tabIndicator} data-chromatic="ignore" />
      </ul>
    </div>
  );
};
